import React, { useState, useEffect, useCallback } from 'react';
import { apiGet } from './apiUtils';
import { getCurrentUser } from './authUtils';
import { Button } from './components/ui/button';
import { AlertCircle, FileText, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from './components/ui/alert';
import DateRangeSelector from './components/DateRangeSelector';
import { useDateRangeSelector } from './hooks/useDateRangeSelector';

function StockLifted() {
  // Get shop name from user
  const user = getCurrentUser();
  const shopName = user.shopName || 'Easy Sheets, Daily';
  // Date range selector hook
  const dateSelector = useDateRangeSelector();
  
  // Extract values for use in dependencies
  const { reportType, selectedDate, selectedYear, selectedMonth, selectedWeek, customStartDate, customEndDate } = dateSelector;

  // Data
  const [brandMap, setBrandMap] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dayWiseData, setDayWiseData] = useState([]);

  // Load master brands for invoice price, MRP, packQuantity
  useEffect(() => {
    const loadMasterBrands = async () => {
      try {
        const res = await apiGet('/api/master-brands');
        const list = await res.json();
        const m = new Map();
        (list || []).forEach(b => {
          m.set(b.id, {
            id: b.id,
            brandNumber: b.brandNumber,
            brandName: b.name,
            size: b.size,
            sizeCode: b.sizeCode,
            packType: b.packType,
            packQuantity: b.packQuantity,
            mrp: b.mrp,
            invoicePrice: b.invoice || 0
          });
        });
        setBrandMap(m);
      } catch (_) {}
    };
    loadMasterBrands();
  }, []);

  // Helpers
  const toDatesArray = (start, end) => {
    const out = [];
    const c = new Date(start);
    const e = new Date(end);
    while (c <= e) {
      const year = c.getFullYear();
      const month = String(c.getMonth() + 1).padStart(2, '0');
      const day = String(c.getDate()).padStart(2, '0');
      out.push(`${year}-${month}-${day}`);
      c.setDate(c.getDate() + 1);
    }
    return out;
  };

  // Fetch and compute lifted data day-wise
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!brandMap || brandMap.size === 0) {
        setLoading(false);
        return;
      }
      const { startDate, endDate } = dateSelector.getDateRange();
      const dates = toDatesArray(startDate, endDate);

      // Fetch received stock for each date
      const allReceived = await Promise.all(dates.map(async d => {
        try {
          const r = await apiGet(`/api/received-stock?date=${d}`);
          return { date: d, data: await r.json() };
        } catch (error) {
          return { date: d, data: { receivedStock: [] } };
        }
      }));

      // Process each day's data
      const dayData = allReceived.map(({ date, data }) => {
        const recs = data.receivedStock || [];
        let invTotal = 0;
        let mrpTotal = 0;
        const items = [];

        for (const rs of recs) {
          const qty = rs.invoice_quantity || 0;
          if (!qty) continue;
          
          const mb = brandMap.get(rs.master_brand_id);
          const invoicePrice = mb?.invoicePrice || 0;
          const mrp = mb?.mrp || rs.mrp_price || 0;
          const packQuantity = mb?.packQuantity || 1;
          
          const invValue = qty * invoicePrice;
          const mrpValue = qty * mrp;
          
          invTotal += invValue;
          mrpTotal += mrpValue;
          
          items.push({
            brandName: mb?.brandName || rs.brand_name || '',
            brandNumber: mb?.brandNumber || rs.brand_number,
            sizeCode: mb?.sizeCode || rs.size_code,
            packQuantity,
            quantity: qty,
            invoicePrice,
            mrp,
            invValue,
            mrpValue
          });
        }

        return {
          date,
          label: new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }),
          invTotal,
          mrpTotal,
          items
        };
      });

      setDayWiseData(dayData);
    } catch (e) {
      setError(e.message || 'Failed to load data');
      setDayWiseData([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandMap, reportType, selectedDate, selectedYear, selectedMonth, selectedWeek, customStartDate, customEndDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calculate grand totals
  const grandTotals = dayWiseData.reduce((acc, day) => ({
    invTotal: acc.invTotal + day.invTotal,
    mrpTotal: acc.mrpTotal + day.mrpTotal
  }), { invTotal: 0, mrpTotal: 0 });

  const generatePDF = () => {
    const { startDate, endDate } = dateSelector.getDateRange();
    const fmt = (d) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
    const monthStr = reportType === 'monthly'
      ? new Date(selectedYear, selectedMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      : '';
    const rangeStr = startDate === endDate ? fmt(startDate) : `${fmt(startDate)} to ${fmt(endDate)}`;
    const formatNum = (n) => Math.round(n).toLocaleString('en-IN');

    // Format date as DD-MM-YYYY
    const formatDateDMY = (dateStr) => {
      const d = new Date(dateStr);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}-${month}-${year}`;
    };

    // Generate day-wise details HTML
    const dayWiseDetailsHtml = dayWiseData
      .filter(day => day.items.length > 0)
      .map((day, dayIdx) => {
        const itemsHtml = day.items.map((item, idx) => {
          const pq = parseInt(item.packQuantity, 10) || 0;
          const cases = pq > 0 ? Math.floor(item.quantity / pq) : 0;
          const bottles = pq > 0 ? (item.quantity % pq) : item.quantity;
          return `
            <tr>
              <td class="center">${idx + 1}</td>
              <td>${item.brandName}</td>
              <td class="center">${item.sizeCode || ''}</td>
              <td class="center">${cases > 0 ? cases : '-'}</td>
              <td class="center">${bottles}</td>
              <td class="right">${formatNum(item.invValue)}</td>
              <td class="right">${formatNum(item.mrpValue)}</td>
            </tr>
          `;
        }).join('');

        return `
          ${dayIdx > 0 ? '<div class="page-break"></div>' : ''}
          <div class="header">
            <div class="shop">${shopName}</div>
            <div class="report">STOCK LIFTED REPORT</div>
            <div class="meta">Date: ${formatDateDMY(day.date)}</div>
          </div>
          <div class="day-section">
            <table>
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Brand</th>
                  <th>Size</th>
                  <th>Cases</th>
                  <th>Bottles</th>
                  <th>Invoice</th>
                  <th>MRP</th>
                </tr>
              </thead>
              <tbody>
                ${itemsHtml}
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="5"><strong>Day Total</strong></td>
                  <td class="right total-red">${formatNum(day.invTotal)}</td>
                  <td class="right total-red">${formatNum(day.mrpTotal)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        `;
      }).join('');

    const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>Stock Lifted Report - ${shopName}</title>
  <style>
    @page { 
      margin: 10mm; 
      size: A4 portrait;
      @top-left { content: ''; }
      @top-center { content: ''; }
      @top-right { content: ''; }
      @bottom-left { content: ''; }
      @bottom-center { content: ''; }
      @bottom-right { content: ''; }
    }
    @media print {
      html, body { margin: 0; padding: 0; }
    }
    body { font-family: Arial, sans-serif; font-size: 11px; }
    .header { text-align: center; margin-bottom: 10px; }
    .shop { font-weight: 800; font-size: 22px; }
    .title { font-weight: 700; font-size: 14px; text-align: center; margin-bottom: 8px; }
    .report { font-weight: 800; font-size: 18px; text-transform: uppercase; }
    .meta { font-weight: 600; font-size: 16px; }
    .month { font-weight: 600; font-size: 14px; }
    table { width: 100%; border-collapse: collapse; margin-top: 8px; }
    th, td { border: 1px solid #000; padding: 4px; text-align: left; }
    th { background: #f0f0f0; text-align: center; }
    .right { text-align: right; }
    .center { text-align: center; }
    .section { margin-top: 14px; }
    .page-break { page-break-before: always; }
    .total-red { color: #d00; font-weight: bold; }
    thead { display: table-header-group; }
    tfoot { display: table-row-group; }
    tfoot tr { page-break-inside: avoid; }
    .summary-table { width: 70%; margin: 10px auto 0 auto; }
    .day-section { margin-top: 14px; }
  </style>
</head>
<body>
  <div class="header">
    <div class="shop">${shopName}</div>
    <div class="report">STOCK LIFTED REPORT</div>
    ${monthStr ? `<div class="month">${monthStr}</div>` : ''}
    <div class="meta">${rangeStr}</div>
  </div>

  <div class="section">
    <div class="title">Stock Lifted - Day Wise Summary</div>
    <table class="summary-table">
      <thead>
        <tr>
          <th>S.No</th>
          <th>Date</th>
          <th>Invoice</th>
          <th>MRP</th>
        </tr>
      </thead>
      <tbody>
        ${dayWiseData.map((day, idx) => `
          <tr>
            <td class="center">${idx + 1}</td>
            <td>${day.label}</td>
            <td class="right">${formatNum(day.invTotal)}</td>
            <td class="right">${formatNum(day.mrpTotal)}</td>
          </tr>
        `).join('')}
      </tbody>
      <tfoot>
        <tr>
          <td colspan="2"><strong>Grand Total</strong></td>
          <td class="right total-red">${formatNum(grandTotals.invTotal)}</td>
          <td class="right total-red">${formatNum(grandTotals.mrpTotal)}</td>
        </tr>
      </tfoot>
    </table>
  </div>

  <div class="page-break"></div>
  ${dayWiseDetailsHtml}

  <div class="section" style="margin-top: 30px;">
    <table class="summary-table">
      <tfoot>
        <tr>
          <td colspan="5"><strong>Grand Total</strong></td>
          <td class="right total-red">${formatNum(grandTotals.invTotal)}</td>
          <td class="right total-red">${formatNum(grandTotals.mrpTotal)}</td>
        </tr>
      </tfoot>
    </table>
  </div>

</body>
</html>
`;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    setTimeout(() => { w.print(); w.close(); }, 600);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="text-muted-foreground">Loading stock lifted data...</span>
        </div>
      </div>
    );
  }

  return (
      <main className="w-full px-4 py-8 space-y-6 max-w-7xl mx-auto">
        
        <div className="space-y-2">
          <h1 className="text-4xl font-bold text-left">Stock Lifted Report</h1>
        <p className="text-muted-foreground text-left">Day-wise stock lifted summary and details</p>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="flex items-center justify-between">
                <span>{error}</span>
                <Button variant="outline" size="sm" onClick={fetchData}>Retry</Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        <DateRangeSelector
          reportType={dateSelector.reportType}
          onReportTypeChange={dateSelector.setReportType}
          selectedDateObj={dateSelector.selectedDateObj}
          onDateSelect={dateSelector.handleDateSelect}
          selectedYear={dateSelector.selectedYear}
          onYearChange={dateSelector.handleYearChange}
          selectedMonth={dateSelector.selectedMonth}
          onMonthChange={dateSelector.handleMonthChange}
          selectedWeek={dateSelector.selectedWeek}
          onWeekChange={dateSelector.handleWeekChange}
          weeksInMonth={dateSelector.weeksInMonth}
          customStartDateObj={dateSelector.customStartDateObj}
          customEndDateObj={dateSelector.customEndDateObj}
          onCustomRangeSelect={dateSelector.handleCustomRangeSelect}
          onGenerateReport={fetchData}
          loading={loading}
          getMaxDate={dateSelector.getCurrentBusinessDateObject}
          hideGenerateButton={true}
        />

        <div className="flex justify-center">
          <Button onClick={generatePDF} size="lg" className="gap-2">
            <FileText className="h-5 w-5" />
            Generate PDF Report
          </Button>
        </div>
    </main>
  );
}

export default StockLifted;
